import React, { FC, memo, useState } from "react";
import { IFormValues } from "components/BaseInput/types";
import { Box, Button, Grid, Typography } from "@mui/material";
import BaseInput from "components/BaseInput";
import MaskedPhoneInput from "components/MaskedPhoneInput/MaskedPhoneInput";
import { useFormik } from "formik";
import { initialValues, phoneLength } from "./constants";
import { useAuthWithSmsMutation, useGetSmsAuthCodeMutation } from "store/api";
import { ISignInProps } from "./types";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { signIn } from "store/slices/auth";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import { findErrorById } from "config/constants";
import timeStyles from "pages/OrderModal/components/TimeSection/TimeSection.styled";
import styles from "./SignIn.styles";

const SignIn: FC<ISignInProps> = ({ onSave }) => {
  const [isCodeSend, setIsCodeSend] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [getSmsAuthCode] = useGetSmsAuthCodeMutation();
  const [authWithSms] = useAuthWithSmsMutation();
  const dispatch = useAppDispatch();

  const formik = useFormik<IFormValues>({
    initialValues,
    onSubmit: async (values) => {
      try {
        const token = await authWithSms({
          phone: values.phone,
          ["confirm_code"]: values.confirmCode,
        }).unwrap();
        dispatch(signIn({ token: token.AccessToken }));
        setIsCodeSend(false);
        onSave();
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: findErrorById(error.data.Id),
        });
      }
    },
  });

  const startTimer = (seconds: number) => {
    setIsButtonDisabled(true);
    setRemainingTime(seconds);

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setIsButtonDisabled(false);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCodeSend = () => {
    const fetchData = async () => {
      try {
        await getSmsAuthCode(formik.values.phone).unwrap();
        setIsCodeSend(true);
        startTimer(60);
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: findErrorById(error.data.Id),
        });
      }
    };

    fetchData();
  };

  return (
    <>
      <Typography component="h2" sx={timeStyles.title}>
        Авторизація
      </Typography>
      <Typography variant="subtitle1" sx={styles.subtitle}>
        Для здійснення замовлення необхідно підтвердити номер телефону
      </Typography>
      <Box sx={styles.formWrap}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12} sx={styles.inputWrap}>
              <MaskedPhoneInput
                id="phone"
                name="phone"
                placeholder="Телефон"
                formik={formik}
              />
            </Grid>
            <Grid item sx={{ ...styles.inputWrap, ...styles.inputsWrap }}>
              <Button
                type="button"
                className="secondary"
                sx={styles.codeBtn}
                disabled={
                  formik.values.phone.length !== phoneLength || isButtonDisabled
                }
                onClick={handleCodeSend}
              >
                Отримати код
              </Button>
              <BaseInput
                id="confirmCode"
                label="Код"
                name="confirmCode"
                type="text"
                formik={formik}
                sx={styles.codeInput}
                disabled={!isCodeSend}
              />
            </Grid>
            {isButtonDisabled && (
              <Grid item xs={12} sx={styles.textWrap}>
                <Typography>{`Повторно відправити код через ${remainingTime}сек.`}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sx={styles.inputWrap}>
              <Button
                type="submit"
                className="primaryBlack"
                sx={styles.button}
                disabled={!formik.values.confirmCode}
              >
                Увійти
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default memo(SignIn);
